import {
  FunctionComponent,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import KidsEditorGridItem from '../../../kidsV2/layout/KidsEditorGridItem.component';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import Raptor2Loading from '../../../../../feedback/Raptor2Loading';
import SaveConfirmationDialogBox from './SaveConfirmationDialogBox.component';
import Button from '@mui/material/Button';
import { KidsData } from '../KidsEditor';
import { useGenerateNewKIDPdf } from '../../services/mutations';
import { KidsDataPdfVersion } from '../../types/KidsData.types';
import { convertToPdfVersion } from '../../utils/viewer.utils';
import { getOS } from '../../../../../../utilities/helpers/common/system';
import { useRaidrKids } from '../../context/RaidrKidsContext';
import { pdfjs } from 'react-pdf';
import { toast } from '../../../../../toast/Toast';
import {
  KidFiguresPartialSuccessResponse,
  useWriteCurrentKidFiguresToDocument,
} from '../../../mutations';
import { useQueryClient } from '@tanstack/react-query';
import { useUpdateKidData, WriteKidDataPayload } from '../../../mutations';
import hotToast from 'react-hot-toast';
import TokenService from '../../../../../../services/token-service';
import RaptorPDFViewer from '../../../../../general/raptorPDFViewer/RaptorPDFViewer';
import { greys, mainColors } from '../../../../../../styling/theme';
import { makeStyles } from '@mui/styles';

export const usePdfViewerStyles = makeStyles(() => ({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between', // Changed to space-between for left/right groups
    padding: '0.47rem 0.62rem',
    backgroundColor: 'white',
    borderBottom: `1px solid ${greys.grey100}`,
    minHeight: '48px',
  },
  toolbarButton: {
    fontSize: '0.56rem',
    height: '1.88rem',
    padding: '0.12rem 0.62rem',
    color: greys.grey800,
    backgroundColor: 'white',
    borderRadius: '0.12rem',
    borderColor: greys.grey200,
    cursor: 'pointer',
    fontWeight: 600,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: greys.grey500,
    },
  },
  primaryButton: {
    fontSize: '0.56rem',
    height: '1.88rem',
    padding: '0.12rem 0.62rem',
    color: 'white',
    backgroundColor: mainColors.mainBlue,
    borderRadius: '0.12rem',
    cursor: 'pointer',
    fontWeight: 600,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: mainColors.mainBlue_lighter,
    },
    '&.Mui-disabled': {
      backgroundColor: greys.grey200,
      color: greys.grey400,
    },
  },
}));

interface EditorPdfViewerProps {
  priipsKidsData: KidsData[];
  isSaveEnabled: boolean;
  getTemplateChangesMade: () => string[];
  getCanUserSave: () => string[];
  resetContentAfterSave: () => void;
  figuresWereUpdated: boolean;
  fileName: string;
}

const EditorPdfViewer = forwardRef<
  { refreshPdf: () => void },
  EditorPdfViewerProps
>(
  (
    {
      priipsKidsData,
      isSaveEnabled,
      getTemplateChangesMade,
      getCanUserSave,
      resetContentAfterSave,
      figuresWereUpdated,
      fileName,
    },
    ref,
  ) => {
    const { iframeTitle } = useRaidrKids();
    const queryClient = useQueryClient();
    const { user_client_id: clientId, user_name } = TokenService.getUser()!;
    const classes = usePdfViewerStyles();
    const createNewKIDPdf = useGenerateNewKIDPdf();

    const [saveDialogIsShowing, setSaveDialogIsShowing] = useState(false);
    const loadingToastId = useRef<string | undefined>(undefined);
    const loadingToastFiguresId = useRef<string | undefined>(undefined);

    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [hasError, setHasError] = useState(false);

    const { mutate, isPending, error } = useUpdateKidData({
      onMutate: () => {
        loadingToastId.current = toast({
          title: 'Saving Data',
          message: `Saving Data.`,
          type: 'loading',
        });
      },
      onSuccess: () => {
        toast({
          title: 'Success!',
          message: `Data saved successfully.`,
          type: 'success',
          duration: 5000,
        });
        // invalidate the kids-overview-data query
        queryClient.invalidateQueries({
          queryKey: ['kids-overview-data'],
        });
      },
      onSettled: () => {
        if (loadingToastId.current) {
          hotToast.dismiss(loadingToastId.current);
          loadingToastId.current = undefined;
        }
        resetContentAfterSave();
      },
    });

    const OS = getOS();

    const {
      mutate: mutateDoc,
      isPending: pendingDoc,
      error: errorDoc,
    } = useWriteCurrentKidFiguresToDocument(clientId, {
      onMutate: () => {
        loadingToastFiguresId.current = toast({
          title: 'Updating Figures...',
          message: `Updating figures. Please wait...`,
          type: 'loading',
        });
      },
      onSuccess: (response) => {
        if (response.status === 207) {
          const data = response.data as KidFiguresPartialSuccessResponse;
          const failedShareClasses = data.failed_share_classes || [];
          toast({
            title: 'Partial Update!',
            message: (
              <span className="flex flex-col">
                <div className="mb-1">
                  Figures were updated for all share classes except:
                </div>
                {failedShareClasses.map((shareClass: string, index: number) => (
                  <div key={index} className="flex">
                    <div className="font-medium">Failed Class:</div>
                    <div className="flex-1 break-words italic">
                      {shareClass}
                    </div>
                  </div>
                ))}
                <div className="mt-2">
                  All other share classes were updated successfully.
                </div>
              </span>
            ),
            type: 'error',
            duration: 2 * 60 * 1000,
          });
        } else {
          toast({
            title: 'Success!',
            message: `Figures updated successfully.`,
            type: 'success',
            duration: 5000,
          });
        }
        // Invalidate the kids-document-figures query
        queryClient.invalidateQueries({
          queryKey: ['kids-document-figures', clientId],
        });
        // invalidate the kids-overview-data query
        queryClient.invalidateQueries({
          queryKey: ['kids-overview-data'],
        });
      },
      onSettled: () => {
        if (loadingToastFiguresId.current) {
          hotToast.dismiss(loadingToastFiguresId.current);
          loadingToastFiguresId.current = undefined;
        }
        refreshPdf();
      },
    });

    // Expose refreshPdf to parent
    useImperativeHandle(ref, () => ({
      refreshPdf,
    }));

    // Initial load
    useEffect(() => {
      refreshPdf();
    }, []);

    // FUNCTIONS
    const refreshPdf = async () => {
      if (!priipsKidsData?.length) return;

      try {
        setHasError(false);
        const content = priipsKidsData.map(convertToPdfVersion);
        const formData = new FormData();
        const priipsKidsJson = JSON.stringify(content);
        const fundId = content[0].fund_id_string;
        const fundName = content[0].fund_name;
        const isin = content[0].share_class;
        const documentLanguage = content[0].document_language;

        formData.append('contents', priipsKidsJson);
        formData.append('fund_id', fundId);
        formData.append('fund_name', fundName);
        formData.append('isin', isin);
        formData.append('document_language', documentLanguage);
        formData.append(
          'params',
          JSON.stringify([
            'contents',
            'fund_id',
            'fund_name',
            'isin',
            'document_language',
          ]),
        );

        const response = await createNewKIDPdf.mutateAsync(formData);

        const blob = new Blob([response.data], { type: 'application/pdf' });
        const fileUrl = window.URL.createObjectURL(blob);

        const iframe = document.querySelector('iframe');
        if (iframe?.src) {
          iframe.src = fileUrl;
          iframe.title = iframeTitle;
        }

        if (pdfUrl) URL.revokeObjectURL(pdfUrl);

        // Setting the worker for pdfjs:
        if (OS != 'linux') {
          pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
        }

        setPdfUrl(fileUrl);
      } catch (error) {
        console.error('Failed to refresh PDF:', error);
        setHasError(true);
      }
    };

    async function handleSaveChanges(
      applyToAllDocuments: boolean,
      applyToAllShareClasses?: boolean,
    ) {
      if (!priipsKidsData.length) {
        return;
      }
      const content: KidsDataPdfVersion[] = [];
      priipsKidsData.forEach((element: any) => {
        content.push(convertToPdfVersion(element));
      });

      const payload: WriteKidDataPayload = {
        user_name,
        kid_data: content,
        template_updates: [],
        increment_version_number: true,
      };

      const fieldIds = getTemplateChangesMade();
      if (fieldIds.length && applyToAllDocuments) {
        const changes: any[] = [];
        fieldIds.forEach((id) => {
          const element = priipsKidsData.find((elem) => elem.fieldId === id);
          changes.push({
            template_id: element?.templateId,
            text: element?.content,
          });
        });
        payload.template_updates = changes;
      }

      mutate(payload);

      // This will be addressed in a seperate PR
      if (applyToAllShareClasses) {
        await handleUpdateFigures(applyToAllShareClasses);
      }
      refreshPdf();
    }

    async function handleUpdateFigures(applyToAllShareClasses: boolean) {
      const payload = {
        fund_id: priipsKidsData[0].fundId,
        share_classes: [priipsKidsData[0].shareClass],
      };
      mutateDoc(payload);
    }

    // Custom toolbar component for editor-specific actions
    const EditorToolbar = () => (
      <>
        <Button
          className={classes.toolbarButton}
          variant="outlined"
          onClick={refreshPdf}
          startIcon={<RefreshIcon sx={{ fontSize: '0.94rem' }} />}
          disableElevation
        >
          Reload
        </Button>
        {saveDialogIsShowing && (
          <SaveConfirmationDialogBox
            setDialogIsShowing={setSaveDialogIsShowing}
            dialogIsShowing={saveDialogIsShowing}
            getTemplateChangesMade={getTemplateChangesMade}
            getCanUserSave={getCanUserSave}
            handleSaveChanges={handleSaveChanges}
            figuresWereUpdated={figuresWereUpdated}
            handleUpdateFigures={handleUpdateFigures}
            priipsKidsData={priipsKidsData}
          />
        )}
        <Button
          className={
            isSaveEnabled ? classes.primaryButton : classes.toolbarButton
          }
          variant={isSaveEnabled ? 'contained' : 'outlined'}
          onClick={() => setSaveDialogIsShowing(true)}
          disabled={!isSaveEnabled || isPending}
          startIcon={<SaveIcon sx={{ fontSize: '0.94rem' }} />}
          disableElevation
        >
          Save
        </Button>
      </>
    );

    return (
      <KidsEditorGridItem xs={12}>
        <RaptorPDFViewer
          pdfUrl={pdfUrl}
          fileName={fileName}
          hasError={hasError}
          errorMessage={createNewKIDPdf.error?.response?.data.detail || ''}
          setHasError={setHasError}
          isPending={createNewKIDPdf.isPending}
          customToolbarComponents={<EditorToolbar />}
        />
      </KidsEditorGridItem>
    );
  },
);

export default EditorPdfViewer;
